import { useEffect, useState } from 'react';
import { Loader2, Plus, X, ArrowUp, ArrowDown, Check, GripVertical, Star } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
    useGetAdminPlansQuery,
    useGetPlanFeaturesQuery,
    useGetPricingQuery,
    useUpdatePlanMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { AdminPlan, AdminPlanFeature } from '@/types';

function formatNaira(value: number) {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        value || 0,
    );
}

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

export default function Plans() {
    const currentUser = useAppSelector(selectCurrentUser);
    const { data: plans = [], isLoading } = useGetAdminPlansQuery();
    // The priced catalog a plan is assembled from.
    const { data: catalog = [] } = useGetPlanFeaturesQuery();
    // The whole-plan discount, so each card can show the price it produces.
    const { data: pricing } = useGetPricingQuery();
    const discountPercent = pricing?.wholePlanDiscountPercent ?? 15;

    const sorted = [...plans].sort((a, b) => a.position - b.position);

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    <div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Plans</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Plan name, the popular badge, which priced features the plan includes, order and visibility.
                            The plan price is the sum of its features, so adding one here raises the price by that
                            amount. Feature prices are set on the Pricing screen.
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
                        </div>
                    ) : sorted.length === 0 ? (
                        <p className="text-sm text-gray-400">No plans found.</p>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                            {sorted.map((plan) => (
                                <PlanEditor
                                    key={plan.id}
                                    plan={plan}
                                    catalog={catalog}
                                    discountPercent={discountPercent}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

function PlanEditor({
    plan,
    catalog,
    discountPercent,
}: {
    plan: AdminPlan;
    catalog: AdminPlanFeature[];
    discountPercent: number;
}) {
    const [updatePlan, { isLoading: isSaving }] = useUpdatePlanMutation();

    const [name, setName] = useState(plan.name);
    const [popular, setPopular] = useState(plan.popular);
    const [isActive, setIsActive] = useState(plan.isActive);
    const [position, setPosition] = useState(String(plan.position ?? 0));
    const [featureIds, setFeatureIds] = useState<string[]>(plan.featureIds ?? []);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    // Re-seed from the server whenever this plan refreshes (e.g. after a save).
    useEffect(() => {
        setName(plan.name);
        setPopular(plan.popular);
        setIsActive(plan.isActive);
        setPosition(String(plan.position ?? 0));
        setFeatureIds(plan.featureIds ?? []);
        setError('');
    }, [plan]);

    const dirty = () => {
        setSaved(false);
        setError('');
    };

    const byId = new Map(catalog.map((f) => [f.id, f]));
    // The plan's features in their assigned order, and what is left to add.
    const chosen = featureIds.map((id) => byId.get(id)).filter((f): f is AdminPlanFeature => !!f);
    const available = catalog.filter((f) => f.isActive && !featureIds.includes(f.id));
    // Standalone total, and the plan price: that sum less the whole-plan
    // discount. Nobody types the plan price; it falls out of the features.
    const separatePrice = chosen.reduce((sum, f) => sum + f.price, 0);
    const planPrice = Math.round(separatePrice * (1 - discountPercent / 100));

    const addFeature = (id: string) => {
        setFeatureIds((prev) => [...prev, id]);
        dirty();
    };
    const removeFeature = (index: number) => {
        setFeatureIds((prev) => prev.filter((_, i) => i !== index));
        dirty();
    };
    const moveFeature = (index: number, dir: -1 | 1) => {
        setFeatureIds((prev) => {
            const target = index + dir;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            const a = next[index]!;
            const b = next[target]!;
            next[index] = b;
            next[target] = a;
            return next;
        });
        dirty();
    };

    const handleSave = async () => {
        setError('');
        setSaved(false);
        if (!name.trim()) {
            setError('Plan name is required.');
            return;
        }
        const pos = Number(position);
        if (!Number.isInteger(pos) || pos < 0) {
            setError('Position must be a whole number of 0 or more.');
            return;
        }
        try {
            await updatePlan({
                id: plan.id,
                data: {
                    name: name.trim(),
                    popular,
                    isActive,
                    position: pos,
                    featureIds,
                },
            }).unwrap();
            setSaved(true);
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to save the plan.'));
        }
    };

    return (
        <div
            className={`rounded-2xl border p-5 flex flex-col gap-4 ${
                isActive ? 'border-gray-100' : 'border-gray-200 bg-gray-50/60'
            }`}
        >
            {/* Header: name + id */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                    <label className="text-[11px] uppercase tracking-wider text-gray-400">Plan name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            dirty();
                        }}
                        className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:border-[#FF6B35]"
                    />
                    <span className="text-[11px] text-gray-400 mt-1 inline-block">id: {plan.id}</span>
                </div>
            </div>

            {/* Toggles + position */}
            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={() => {
                        setPopular((v) => !v);
                        dirty();
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        popular
                            ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                    <Star className={`w-3.5 h-3.5 ${popular ? 'fill-[#FF6B35]' : ''}`} />
                    Popular
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setIsActive((v) => !v);
                        dirty();
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        isActive
                            ? 'border-green-200 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                >
                    {isActive ? 'Visible' : 'Hidden'}
                </button>
                <label className="flex items-center gap-2 text-xs text-gray-500 ml-auto">
                    Position
                    <input
                        type="text"
                        inputMode="numeric"
                        value={position}
                        onChange={(e) => {
                            setPosition(e.target.value.replace(/[^\d]/g, ''));
                            dirty();
                        }}
                        className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-[#FF6B35]"
                    />
                </label>
            </div>

            {/* Features — the priced lines this plan is made of */}
            <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                    <label className="text-[11px] uppercase tracking-wider text-gray-400">Features</label>
                    <span className="text-[11px] text-gray-400">{chosen.length} selected</span>
                </div>
                {chosen.length === 0 ? (
                    <p className="text-xs text-gray-400">No features yet. This plan would cost ₦0.</p>
                ) : (
                    chosen.map((feature, index) => (
                        <div key={feature.id} className="flex items-center gap-1.5">
                            <GripVertical className="w-4 h-4 text-gray-300 shrink-0" />
                            <div className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2">
                                <p className="text-sm text-gray-900 truncate">{feature.label}</p>
                                <p className="text-[11px] text-gray-400">{formatNaira(feature.price)}</p>
                            </div>
                            <div className="flex items-center">
                                <button
                                    type="button"
                                    onClick={() => moveFeature(index, -1)}
                                    disabled={index === 0}
                                    className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                    title="Move up"
                                >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveFeature(index, 1)}
                                    disabled={index === chosen.length - 1}
                                    className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                    title="Move down"
                                >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeFeature(index)}
                                    className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    title={`Remove — the plan drops by ${formatNaira(feature.price)}`}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))
                )}

                {/* Adding a feature raises the plan price by exactly its price. */}
                {available.length > 0 && (
                    <label className="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#FF6B35]">
                        <Plus className="w-3.5 h-3.5" />
                        <select
                            value=""
                            onChange={(e) => e.target.value && addFeature(e.target.value)}
                            className="flex-1 cursor-pointer border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-[#FF6B35]"
                        >
                            <option value="">Add a feature…</option>
                            {available.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.label} · {formatNaira(f.price)}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                {/* The plan price, derived. Nobody types this. */}
                <div className="mt-2 flex flex-col gap-1 rounded-xl bg-gray-50 px-3 py-2.5">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Bought separately</span>
                        <span className="tabular-nums">{formatNaira(separatePrice)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-medium text-[#FF6B35]">
                        <span>Whole plan saving ({discountPercent}%)</span>
                        <span className="tabular-nums">−{formatNaira(separatePrice - planPrice)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-1.5">
                        <span className="text-xs font-medium text-gray-600">Plan price</span>
                        <span className="text-sm font-bold text-gray-900 tabular-nums">{formatNaira(planPrice)}</span>
                    </div>
                </div>
                <p className="text-[11px] text-gray-400">
                    The features above, less the whole plan discount. Change it by adding or removing features, or by
                    re-pricing a feature on the Pricing screen.
                </p>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="self-start bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
            >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
                {saved ? 'Saved' : 'Save plan'}
            </button>
        </div>
    );
}
